import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteSidebar from "@/components/feature/SiteSidebar";
import SiteFooter from "@/components/feature/SiteFooter";
import SearchBox from "@/components/feature/SearchBox";
import FavoritesMenu from "@/components/feature/FavoritesMenu";
import { NAV_ITEMS } from "@/components/feature/navItems";

interface SiteLayoutProps {
  children: ReactNode;
}

export default function SiteLayout({ children }: SiteLayoutProps) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
    setSearchOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen, searchOpen]);

  const isActive = (item: (typeof NAV_ITEMS)[number]) =>
    item.matches.some((match) =>
      match === "/" ? location.pathname === "/" : location.pathname.startsWith(match),
    );

  return (
    <div className="min-h-screen bg-background-100">
      <SiteSidebar />

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 hidden h-16 items-center gap-4 border-b border-background-200/80 bg-background-50/90 px-6 backdrop-blur lg:flex xl:px-8">
          <div className="flex shrink-0 items-center gap-2 font-label text-xs text-foreground-500">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-database-2-line" aria-hidden="true"></i>
            </span>
            日本女优资料索引
          </div>
          <div className="flex flex-1 justify-center px-2">
            <div className="w-full max-w-[400px]">
              <SearchBox />
            </div>
          </div>
          <div className="shrink-0">
            <FavoritesMenu />
          </div>
        </header>

        <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-background-200/80 bg-background-50/95 px-4 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="打开主导航"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800"
          >
            <i className="ri-menu-line text-lg" aria-hidden="true"></i>
          </button>
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-primary-500 font-heading text-[11px] font-bold text-background-50">
              日
            </span>
            <span className="font-heading text-base font-black text-foreground-950">日本女优</span>
          </Link>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="打开搜索"
            className="ml-auto flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800"
          >
            <i className="ri-search-line text-base" aria-hidden="true"></i>
          </button>
        </div>

        <main key={location.pathname} className="mx-auto w-full max-w-[1180px] animate-fade-in px-4 pb-14 pt-6 md:px-8 md:pt-8">
          {children}
        </main>

        <SiteFooter />
      </div>

      {drawerOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/45"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute inset-y-0 left-0 flex w-[286px] max-w-[82vw] flex-col bg-background-50">
            <div className="flex h-16 items-center justify-between border-b border-background-200 px-4">
              <span className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded bg-primary-500 font-heading text-xs font-bold text-background-50">
                  日
                </span>
                <span className="font-heading text-lg font-black text-foreground-950">日本女优</span>
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭导航"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4">
              <ul className="space-y-1">
                {NAV_ITEMS.map((item) => {
                  const active = isActive(item);
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-3 rounded-md px-3 py-3 font-label text-sm transition-colors ${
                          active
                            ? "bg-primary-50 font-medium text-primary-800"
                            : "text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        <span className="flex h-5 w-5 items-center justify-center">
                          <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                        </span>
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4">
                <p className="font-label text-xs font-medium text-foreground-800">收藏与检索</p>
                <p className="mt-1.5 font-label text-[11px] leading-relaxed text-foreground-500">
                  收藏状态仅保存在当前浏览器，属于前端演示功能。搜索支持女优姓名与作品名称联想。
                </p>
              </div>
            </nav>
            <div className="border-t border-background-200 p-4">
              <a
                href="mailto:archive@japan-actress.example"
                className="flex items-center gap-2 font-label text-xs text-foreground-600"
              >
                <i className="ri-mail-line" aria-hidden="true"></i>
                archive@japan-actress.example
              </a>
            </div>
          </div>
        </div>
      ) : null}

      {searchOpen ? (
        <div className="fixed inset-0 z-[60] bg-background-50 lg:hidden">
          <div className="flex h-14 items-center gap-3 border-b border-background-200 px-4">
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              aria-label="关闭搜索"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700"
            >
              <i className="ri-arrow-left-line text-lg" aria-hidden="true"></i>
            </button>
            <span className="font-label text-sm font-medium text-foreground-900">站内搜索</span>
          </div>
          <div className="px-4 pt-4">
            <SearchBox autoFocus large onNavigate={() => setSearchOpen(false)} />
            <p className="mt-6 font-label text-[11px] tracking-wide text-foreground-400">热门检索</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {["绫濑真央", "神宫寺葵", "白夜的花火", "东京的四个季节", "静默的证词"].map((word) => (
                <Link
                  key={word}
                  to="/category"
                  onClick={() => setSearchOpen(false)}
                  className="rounded-full border border-background-300 px-3 py-1.5 font-label text-xs text-foreground-700"
                >
                  {word}
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}